// src/utils/voiceSelection.js
//
// Brand Settings' voice editor, kept free of React, Vite and Supabase so it can be
// tested in plain Node: how a voice loads into the editor fields, how the fields
// become the profile that is saved, and how Save decides what to write.
//
// Save Settings used to rewrite every voice the user owns (saveProfile). An approved
// voice is approved for its exact stored text, so that rewrite un-approved voices
// even when the only change was which voice is active (Handoff live voice check,
// 2026-10-30). Now a save that changes nothing but the active voice writes only the
// active-voice setting; any real voice change still goes through saveProfile.

import { normalizeVoiceProfile } from "./voiceProfile.js";

export function linesToArray(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function arrayToLines(value) {
  return Array.isArray(value) ? value.join("\n") : "";
}

/** The editor fields for a voice, as Brand Settings loads them. */
export function editorFieldsFromVoice(voice) {
  const profile = normalizeVoiceProfile(voice?.profile || voice?.text || "");
  const helper = profile.helper_notes || {};

  return {
    voiceName: voice?.name || profile.voice_name || "",
    summary: profile.summary || "",
    playfulnessLevel: profile.playfulness_level ?? 0.35,
    snarkLevel: profile.snark_level ?? 0.3,
    warmthLevel: profile.warmth_level ?? 0.65,
    formalityLevel: profile.formality_level ?? 0.55,
    emojiTolerance: profile.emoji_tolerance || "low",
    ctaStyle: profile.cta_style || "direct",
    styleRulesText: arrayToLines(profile.style_rules),
    negativePromptText: arrayToLines(profile.negative_prompt),
    bannedPhrasesText: arrayToLines(profile.banned_phrases),
    samplePhrasesText: arrayToLines(profile.sample_phrases),
    avoidPhrasesText: arrayToLines(profile.avoid_phrases),
    helperDesired: arrayToLines(helper.desired),
    helperAvoid: arrayToLines(helper.avoid),
    helperSay: arrayToLines(helper.say),
    helperNeverSay: arrayToLines(helper.neverSay),
  };
}

/** The profile the editor fields describe, as Brand Settings saves it. */
export function buildEditorProfile(fields) {
  return normalizeVoiceProfile({
    voice_name: fields.voiceName,
    summary: fields.summary,
    playfulness_level: fields.playfulnessLevel,
    snark_level: fields.snarkLevel,
    warmth_level: fields.warmthLevel,
    formality_level: fields.formalityLevel,
    emoji_tolerance: fields.emojiTolerance,
    cta_style: fields.ctaStyle,
    style_rules: linesToArray(fields.styleRulesText),
    negative_prompt: linesToArray(fields.negativePromptText),
    banned_phrases: linesToArray(fields.bannedPhrasesText),
    sample_phrases: linesToArray(fields.samplePhrasesText),
    avoid_phrases: linesToArray(fields.avoidPhrasesText),
    helper_notes: {
      desired: linesToArray(fields.helperDesired),
      avoid: linesToArray(fields.helperAvoid),
      say: linesToArray(fields.helperSay),
      neverSay: linesToArray(fields.helperNeverSay),
    },
    source_confidence: "manual",
  });
}

/** The voices with the editor's fields applied to the active one. */
export function applyEditorToVoices(voices, activeVoiceId, fields) {
  if (!activeVoiceId) return voices;

  const nextProfile = buildEditorProfile(fields);

  return voices.map((v) =>
    v.id === activeVoiceId
      ? {
          ...v,
          name: fields.voiceName || v.name || "Voice",
          text: nextProfile.summary || "",
          profile: nextProfile,
          updated_at: new Date().toISOString(),
        }
      : v
  );
}

// What the editor would save for a voice: its name and the profile its fields
// produce. Loading a voice and saving it untouched gives the same value, so two
// equal values mean the user changed nothing in that voice.
function editorContent(voice) {
  return JSON.stringify([voice?.name ?? null, buildEditorProfile(editorFieldsFromVoice(voice))]);
}

/**
 * Decide what Save Settings must write.
 *
 * "active_only": the voices are the ones that were loaded, each with exactly the
 * content it was loaded with, so only the active-voice choice can have changed.
 * "full": anything else (a voice added, removed, renamed or edited, or nothing
 * loaded to compare with), which keeps the existing saveProfile behaviour.
 */
export function planBrandSettingsSave({ loadedVoices, voices, activeVoiceId }) {
  const loaded = Array.isArray(loadedVoices) ? loadedVoices : [];
  const current = Array.isArray(voices) ? voices : [];

  if (!loaded.length) return { kind: "full", reason: "nothing_loaded" };

  const loadedById = new Map(loaded.map((v) => [v.id, v]));
  if (loadedById.size !== loaded.length) return { kind: "full", reason: "duplicate_ids" };
  if (current.length !== loaded.length) return { kind: "full", reason: "voices_added_or_removed" };

  for (const voice of current) {
    const before = loadedById.get(voice?.id);
    if (!before) return { kind: "full", reason: "voices_added_or_removed" };
    if (editorContent(voice) !== editorContent(before)) {
      return { kind: "full", reason: "voice_edited" };
    }
  }

  if (!activeVoiceId || !loadedById.has(activeVoiceId)) {
    return { kind: "full", reason: "unknown_active_voice" };
  }

  return { kind: "active_only", activeVoiceId };
}

/**
 * Persist Brand Settings: only the active-voice setting when that is all that
 * changed, otherwise the full saveProfile. Throws if the write fails, so the caller
 * shows success only after this resolves.
 */
export async function persistBrandSettings(
  { loadedVoices, voices, activeVoiceId, snarkLevel },
  { saveActiveVoice, saveProfile }
) {
  const plan = planBrandSettingsSave({ loadedVoices, voices, activeVoiceId });

  if (plan.kind === "active_only") {
    await saveActiveVoice(plan.activeVoiceId);
  } else {
    await saveProfile(null, {
      snark_level: snarkLevel,
      voices,
      active_voice_id: activeVoiceId,
    });
  }

  return plan;
}
